import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ComboWrap,
  ComboButton,
  ComboCaret,
  ComboMenu,
  ComboSearch,
  ComboList,
  ComboOption,
} from '../styles/CustomerSpecsIndexStyles';

function norm(s) {
  return String(s ?? '').trim().toLowerCase();
}

function isOrderedSubsequence(haystack, needle) {
  if (!needle) return true;
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) i += 1;
  }
  return i === needle.length;
}

/**
 * Type-to-filter combobox for enterprise forms.
 * - case-insensitive filtering
 * - arrow up/down navigation
 * - enter selects highlighted option
 * - escape closes
 */
export default function TypeaheadSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  size = 'md', // 'sm' | 'md' | 'lg' (affects max-width only)
  ariaLabel,
  ariaRequired,
  ariaInvalid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useMemo(() => `typeahead-${Math.random().toString(36).slice(2)}`, []);
  const [menuPos, setMenuPos] = useState(null); // { top,left,width,maxHeight }

  const selected = useMemo(() => {
    const v = String(value ?? '');
    return (options || []).find((o) => String(o.value) === v) || null;
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = norm(query);
    const arr = Array.isArray(options) ? options : [];
    if (!q) return arr;
    const exact = [];
    const starts = [];
    const contains = [];
    const subseq = [];
    for (const o of arr) {
      const label = norm(o.label);
      if (!label) continue;
      if (label === q) exact.push(o);
      else if (label.startsWith(q)) starts.push(o);
      else if (label.includes(q)) contains.push(o);
      else if (isOrderedSubsequence(label, q)) subseq.push(o);
    }
    const byLabel = (a, b) => String(a.label || '').localeCompare(String(b.label || ''));
    exact.sort(byLabel);
    starts.sort(byLabel);
    contains.sort(byLabel);
    subseq.sort(byLabel);
    return [...exact, ...starts, ...contains, ...subseq];
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    const onDocMouseDown = (e) => {
      try {
        if (!open) return;
        const t = e.target;
        if (wrapRef.current && wrapRef.current.contains(t)) return;
        if (menuRef.current && menuRef.current.contains(t)) return;
        setOpen(false);
      } catch {}
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const updateMenuPos = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 6;
    const margin = 10;
    const desiredMax = 360;

    // If the trigger is offscreen, close instead of leaving a floating menu.
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setOpen(false);
      return;
    }

    const belowTop = rect.bottom + gap;
    const belowAvail = window.innerHeight - belowTop - margin;
    const aboveAvail = rect.top - gap - margin;
    const shouldPlaceAbove = belowAvail < 160 && aboveAvail > belowAvail;

    if (shouldPlaceAbove) {
      const maxHeight = Math.max(140, Math.min(desiredMax, aboveAvail));
      const top = Math.max(margin, rect.top - gap - maxHeight);
      setMenuPos({
        top,
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - rect.width - margin)),
        width: rect.width,
        maxHeight,
      });
      return;
    }

    const maxHeight = Math.max(140, Math.min(desiredMax, belowAvail));
    const top = Math.min(window.innerHeight - margin, belowTop);
    setMenuPos({
      top,
      left: Math.max(margin, Math.min(rect.left, window.innerWidth - rect.width - margin)),
      width: rect.width,
      maxHeight,
    });
  }, []);

  // Keep the portal menu positioned on scroll/resize (including scroll containers).
  useEffect(() => {
    if (!open) return;
    updateMenuPos();
    const onScrollOrResize = () => updateMenuPos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPos]);

  // When opening, focus search and align activeIndex to selected.
  useEffect(() => {
    if (!open) return;
    setActiveIndex(() => {
      if (!selected) return 0;
      const idx = filtered.findIndex((o) => String(o.value) === String(selected.value));
      return idx >= 0 ? idx : 0;
    });
    const t = window.setTimeout(() => {
      // preventScroll: focusing the portaled filter must not scroll it into view
      // (that nudge is the "section container moves slightly on open" jank).
      try { searchRef.current?.focus?.({ preventScroll: true }); } catch {}
    }, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If filtering shrinks list, clamp activeIndex.
  useEffect(() => {
    if (!open) return;
    setActiveIndex((i) => Math.max(0, Math.min(i, Math.max(0, filtered.length - 1))));
  }, [filtered.length, open]);

  const labelText = selected ? selected.label : placeholder;

  const maxWidth = size === 'sm' ? 260 : size === 'md' ? 420 : 9999;
  const buttonStyle = size === 'lg'
    ? undefined
    : { maxWidth, width: '100%' };

  const menuNode = open && !disabled ? (
    <ComboMenu
      ref={menuRef}
      role="listbox"
      id={listId}
      style={{
        position: 'fixed',
        zIndex: 5000,
        top: menuPos?.top ?? 0,
        left: menuPos?.left ?? 0,
        width: menuPos?.width ?? undefined,
        maxHeight: menuPos?.maxHeight ?? undefined,
        visibility: menuPos ? 'visible' : 'hidden',
        pointerEvents: menuPos ? 'auto' : 'none',
      }}
      onMouseDown={(e) => {
        // Prevent the outside-click handler from interpreting menu clicks as "outside"
        // in some edge cases (e.g., when events retarget).
        e.stopPropagation();
      }}
    >
      <ComboSearch
        ref={searchRef}
        placeholder="Type to filter..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(0, i - 1));
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            const opt = filtered[activeIndex];
            if (!opt) return;
            onChange?.(opt.value);
            setOpen(false);
            setQuery('');
          }
        }}
      />

      <ComboList>
        {filtered.length === 0 ? (
          <div style={{ padding: '8px 10px', color: 'var(--shadow-a55)' }}>No matches</div>
        ) : (
          filtered.map((opt, idx) => {
            const isActive = idx === activeIndex;
            const isSelected = String(opt.value) === String(value ?? '');
            return (
              <ComboOption
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                $active={isActive}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => {
                  onChange?.(opt.value);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {opt.label}
              </ComboOption>
            );
          })
        )}
      </ComboList>
    </ComboMenu>
  ) : null;

  return (
    <ComboWrap ref={wrapRef} style={buttonStyle}>
      <ComboButton
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel || placeholder}
        aria-required={ariaRequired || undefined}
        aria-invalid={ariaInvalid || undefined}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
          setQuery('');
        }}
      >
        <span style={{ color: selected ? undefined : 'var(--shadow-a55)' }}>{labelText}</span>
        <ComboCaret>▾</ComboCaret>
      </ComboButton>
      {menuNode ? createPortal(menuNode, document.body) : null}
    </ComboWrap>
  );
}

